import { useState, useEffect } from "react";
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { apiCall } from "../api";
import { useAuth } from "../context/AuthContext";
import { calculateTimeOnPlatform } from "../utils/time";

// Analytics Dashboard Component  
const AnalyticsDashboard = ({ token }) => {
  const { user } = useAuth();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [tenantManagementData, setTenantManagementData] = useState([]);
  const [evaluationsData, setEvaluationsData] = useState(null);
  const [portfolioData, setPortfolioData] = useState(null);
  const [enhancedInsights, setEnhancedInsights] = useState(null);
  const [comparativeStats, setComparativeStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview');
  const [filters, setFilters] = useState({
    search: '',
    sortBy: 'tenant_name',
    sortOrder: 'asc',
    minRating: '',
    maxRating: ''
  });

  useEffect(() => {
    loadAnalyticsData();
  }, []);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      // Load all analytics data in parallel
      const [overview, tenantMgmt, evaluations, insights, comparative] = await Promise.all([
        apiCall('/analytics/overview', 'GET', null, token),
        apiCall('/analytics/tenant-management', 'GET', null, token),
        apiCall('/analytics/my-evaluations', 'GET', null, token),
        apiCall('/analytics/enhanced-insights', 'GET', null, token),
        apiCall('/analytics/comparative-statistics', 'GET', null, token)
      ]);
      
      console.log(' Enhanced Insights Data:', insights);
      console.log(' Comparative Stats Data:', comparative);
      
      setAnalyticsData(overview);
      setTenantManagementData(tenantMgmt);
      setEvaluationsData(evaluations);
      setPortfolioData(null); // No longer used - replaced by evaluationsData
      setEnhancedInsights(insights);
      setComparativeStats(comparative);
    } catch (error) {
      console.error('Failed to load analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSampleData = async () => {
    try {
      setLoading(true);
      await apiCall('/sample-data/generate', 'POST', {}, token);
      // Reload analytics data after generating sample data
      await loadAnalyticsData();
      alert('\u00a1Datos de muestra generados exitosamente! Los an\u00e1lisis ahora muestran informaci\u00f3n de ejemplo.');
    } catch (error) {
      console.error('Failed to generate sample data:', error);
      alert('Error al generar datos de muestra');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.sortBy) params.append('sort_by', filters.sortBy);
      if (filters.sortOrder) params.append('sort_order', filters.sortOrder);
      if (filters.minRating) params.append('min_rating', filters.minRating);
      if (filters.maxRating) params.append('max_rating', filters.maxRating);
      
      const result = await apiCall(`/analytics/tenant-management?${params.toString()}`, 'GET', null, token);
      setTenantManagementData(result);
    } catch (error) {
      console.error('Failed to filter tenant data:', error);
    }
  };

  useEffect(() => {
    if (activeView === 'tenants') {
      handleFilterChange();
    }
  }, [filters, activeView]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
        <p className="text-amber-600 mt-4">Cargando an&aacute;lisis...</p>
      </div>
    );
  }

  // Chart configurations
  const ratingDistributionData = analyticsData ? {
    labels: ['Excelente (5)', 'Bueno (4)', 'Regular (3)', 'Malo (2)', 'Muy Malo (1)'],
    datasets: [{
      data: [
        analyticsData.rating_distribution.excellent,
        analyticsData.rating_distribution.good,
        analyticsData.rating_distribution.average,
        analyticsData.rating_distribution.poor,
        analyticsData.rating_distribution.very_poor
      ],
      backgroundColor: [
        '#10B981', // Green for excellent
        '#59C15D', // Light green for good
        '#F59E0B', // Amber for average
        '#EF4444', // Red for poor
        '#B91C1C'  // Dark red for very poor
      ],
      borderWidth: 0
    }]
  } : null;

  const monthlyActivityData = analyticsData ? {
    labels: analyticsData.monthly_activity.map(item => item.month),
    datasets: [{
      label: 'Evaluaciones Realizadas',
      data: analyticsData.monthly_activity.map(item => item.count),
      borderColor: '#D97706',
      backgroundColor: 'rgba(217, 119, 6, 0.1)',
      borderWidth: 3,
      pointBackgroundColor: '#D97706',
      pointBorderColor: '#92400E',
      pointBorderWidth: 2,
      pointRadius: 6,
      tension: 0.4
    }]
  } : null;

  const categoryAveragesData = analyticsData ? {
    labels: ['Paga', 'Cuidado', 'Comunicaci\u00f3n', 'Limpieza', 'Ruido', 'Cumplimiento'],
    datasets: [{
      label: 'Puntuaci\u00f3n Promedio',
      data: [
        analyticsData.category_averages.pago_puntual,
        analyticsData.category_averages.cuidado_propiedad,
        analyticsData.category_averages.comunicacion,
        analyticsData.category_averages.limpieza,
        analyticsData.category_averages.nivel_ruido,
        analyticsData.category_averages.cumplimiento_normas
      ],
      backgroundColor: 'rgba(217, 119, 6, 0.8)',
      borderColor: '#92400E',
      borderWidth: 1
    }]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true
        }
      }
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-amber-800 mb-4">Panel de An&aacute;lisis Avanzado</h2>
        <p className="text-amber-600 max-w-3xl mx-auto">
          Obt&eacute;n insights detallados sobre tu actividad como propietario, gesti&oacute;n de inquilinos y rendimiento de propiedades.
        </p>
        
        {/* Sample Data Button */}
        {(!analyticsData || analyticsData.total_ratings === 0) && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-blue-800 font-semibold mb-2">&iquest;Primera vez usando el panel?</h3>
            <p className="text-blue-600 text-sm mb-4">
              Genera datos de muestra para ver c&oacute;mo funciona el panel de an&aacute;lisis con 3 perfiles de inquilinos ficticios.
            </p>
            <button
              onClick={generateSampleData}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Generar Datos de Muestra
            </button>
          </div>
        )}
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-amber-200">
        <nav className="flex space-x-4 sm:space-x-8 overflow-x-auto no-scrollbar" aria-label="Analytics Tabs">
          <button
            onClick={() => setActiveView('overview')}
            className={`flex-shrink-0 py-4 px-3 sm:px-6 border-b-2 font-medium text-sm transition-all duration-200 ${
              activeView === 'overview'
                ? 'border-amber-500 text-amber-700'
                : 'border-transparent text-amber-600 hover:text-amber-700 hover:border-amber-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="whitespace-nowrap">Resumen</span>
            </div>
          </button>
          <button
            onClick={() => setActiveView('tenants')}
            className={`flex-shrink-0 py-4 px-3 sm:px-6 border-b-2 font-medium text-sm transition-all duration-200 ${
              activeView === 'tenants'
                ? 'border-amber-500 text-amber-700'
                : 'border-transparent text-amber-600 hover:text-amber-700 hover:border-amber-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
              <span className="whitespace-nowrap">Gesti&oacute;n de Inquilinos</span>
            </div>
          </button>
          <button
            onClick={() => setActiveView('evaluations')}
            className={`flex-shrink-0 py-4 px-3 sm:px-6 border-b-2 font-medium text-sm transition-all duration-200 ${
              activeView === 'evaluations'
                ? 'border-amber-500 text-amber-700'
                : 'border-transparent text-amber-600 hover:text-amber-700 hover:border-amber-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="whitespace-nowrap">Tus Evaluaciones</span>
            </div>
          </button>
          <button
            onClick={() => setActiveView('insights')}
            className={`flex-shrink-0 py-4 px-3 sm:px-6 border-b-2 font-medium text-sm transition-all duration-200 ${
              activeView === 'insights'
                ? 'border-amber-500 text-amber-700'
                : 'border-transparent text-amber-600 hover:text-amber-700 hover:border-amber-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="whitespace-nowrap">An&aacute;lisis Estad&iacute;stico</span>
            </div>
          </button>
          <button
            onClick={() => setActiveView('comparative')}
            className={`flex-shrink-0 py-4 px-3 sm:px-6 border-b-2 font-medium text-sm transition-all duration-200 ${
              activeView === 'comparative'
                ? 'border-amber-500 text-amber-700'
                : 'border-transparent text-amber-600 hover:text-amber-700 hover:border-amber-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="whitespace-nowrap">Comparativa</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Content based on active view */}
      {activeView === 'overview' && analyticsData && (
        <div className="space-y-8">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center">
                <svg className="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
                <div>
                  <p className="text-amber-100 text-sm">Total Evaluaciones</p>
                  <p className="text-2xl font-bold">{analyticsData.total_ratings}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center">
                <svg className="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                <div>
                  <p className="text-green-100 text-sm">Tiempo en la Plataforma</p>
                  <p className="text-2xl font-bold">{calculateTimeOnPlatform(user?.created_at)}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center">
                <svg className="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <div>
                  <p className="text-blue-100 text-sm">Puntuaci&oacute;n Promedio</p>
                  <p className="text-2xl font-bold">{analyticsData.average_rating_given}/5</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center">
                <svg className="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <div>
                  <p className="text-purple-100 text-sm">Propiedades</p>
                  <p className="text-2xl font-bold">{analyticsData.property_performance.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Rating Distribution Chart */}
            <div className="bg-white rounded-xl shadow-lg border border-amber-200 p-6">
              <h3 className="text-xl font-bold text-amber-800 mb-4">Distribuci&oacute;n de Calificaciones</h3>
              <div className="h-64">
                {ratingDistributionData && (
                  <Doughnut data={ratingDistributionData} options={chartOptions} />
                )}
              </div>
            </div>

            {/* Monthly Activity Chart */}
            <div className="bg-white rounded-xl shadow-lg border border-amber-200 p-6">
              <h3 className="text-xl font-bold text-amber-800 mb-4">Actividad Mensual</h3>
              <div className="h-64">
                {monthlyActivityData && (
                  <Line data={monthlyActivityData} options={chartOptions} />
                )}
              </div>
            </div>
          </div>

          {/* Category Averages Chart */}
          <div className="bg-white rounded-xl shadow-lg border border-amber-200 p-6">
            <h3 className="text-xl font-bold text-amber-800 mb-4">Promedios por Categor&iacute;a</h3>
            <div className="h-64">
              {categoryAveragesData && (
                <Bar data={categoryAveragesData} options={{
                  ...chartOptions,
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 5,
                      ticks: {
                        stepSize: 1
                      }
                    }
                  }
                }} />
              )}
            </div>
          </div>

          {/* Property Performance Table */}
          <div className="bg-white rounded-xl shadow-lg border border-amber-200 p-6">
            <h3 className="text-xl font-bold text-amber-800 mb-4">Rendimiento por Propiedad</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-amber-200">
                <thead className="bg-amber-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Direcci&oacute;n
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Calificaci&oacute;n Promedio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Total Evaluaciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-amber-200">
                  {analyticsData.property_performance.slice(0, 5).map((property, index) => (
                    <tr key={index} className="hover:bg-amber-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-amber-900">
                        {property.address}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-amber-900 mr-2">
                            {property.average_rating}/5
                          </span>
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <svg
                                key={star}
                                className={`w-4 h-4 ${
                                  star <= property.average_rating ? 'text-yellow-400' : 'text-gray-300'
                                }`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-amber-900">
                        {property.total_ratings}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Tenant Management View */}
      {activeView === 'tenants' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="bg-white rounded-xl shadow-lg border border-amber-200 p-6">
            <h3 className="text-xl font-bold text-amber-800 mb-4">Filtros y B&uacute;squeda</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-2">Buscar</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="w-full px-3 py-2 border border-amber-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="Nombre, email, tel&eacute;fono..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-2">Ordenar por</label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                  className="w-full px-3 py-2 border border-amber-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="tenant_name">Nombre</option>
                  <option value="average_rating">Calificaci&oacute;n</option>
                  <option value="total_ratings">Total Evaluaciones</option>
                  <option value="last_rated">&Uacute;ltima Evaluaci&oacute;n</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-2">Orden</label>
                <select
                  value={filters.sortOrder}
                  onChange={(e) => setFilters(prev => ({ ...prev, sortOrder: e.target.value }))}
                  className="w-full px-3 py-2 border border-amber-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="asc">Ascendente</option>
                  <option value="desc">Descendente</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-2">Calificaci&oacute;n M&iacute;n.</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  step="0.1"
                  value={filters.minRating}
                  onChange={(e) => setFilters(prev => ({ ...prev, minRating: e.target.value }))}
                  className="w-full px-3 py-2 border border-amber-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="1.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-2">Calificaci&oacute;n M&aacute;x.</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  step="0.1"
                  value={filters.maxRating}
                  onChange={(e) => setFilters(prev => ({ ...prev, maxRating: e.target.value }))}
                  className="w-full px-3 py-2 border border-amber-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="5.0"
                />
              </div>
            </div>
          </div>

          {/* Tenant Management Table */}
          <div className="bg-white rounded-xl shadow-lg border border-amber-200 p-6">
            <h3 className="text-xl font-bold text-amber-800 mb-4">Gesti&oacute;n de Inquilinos ({tenantManagementData.length})</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-amber-200">
                <thead className="bg-amber-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Inquilino
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Calificaci&oacute;n Promedio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Evaluaciones
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Propiedades
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      &Uacute;ltima Evaluaci&oacute;n
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-amber-200">
                  {tenantManagementData.map((tenant, index) => (
                    <tr key={tenant.id} className="hover:bg-amber-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-amber-900">{tenant.tenant_name}</div>
                          <div className="text-sm text-amber-500">{tenant.tenant_email}</div>
                          {tenant.tenant_phone && (
                            <div className="text-sm text-amber-500">{tenant.tenant_phone}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-amber-900 mr-2">
                            {tenant.average_rating_from_me}/5
                          </span>
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <svg
                                key={star}
                                className={`w-4 h-4 ${
                                  star <= tenant.average_rating_from_me ? 'text-yellow-400' : 'text-gray-300'
                                }`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-amber-900">
                        {tenant.total_ratings_from_me}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-amber-900">
                        {tenant.properties_rented}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-amber-900">
                        {tenant.last_rated}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          {tenant.has_disputes && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              Con disputas
                            </span>
                          )}
                          {tenant.total_evidence_files > 0 && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {tenant.total_evidence_files} archivo(s)
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Portfolio View */}
      {activeView === 'portfolio' && portfolioData && (
        <div className="space-y-6">
          {/* Portfolio Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center">
                <svg className="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <div>
                  <p className="text-amber-100 text-sm">Total Propiedades</p>
                  <p className="text-2xl font-bold">{portfolioData.total_properties}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center">
                <svg className="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                <div>
                  <p className="text-green-100 text-sm">Inquilinos Gestionados</p>
                  <p className="text-2xl font-bold">{portfolioData.total_tenants_managed}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center">
                <svg className="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <div>
                  <p className="text-blue-100 text-sm">Calificaci&oacute;n Promedio</p>
                  <p className="text-2xl font-bold">{portfolioData.average_property_rating}/5</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center">
                <svg className="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                <div>
                  <p className="text-purple-100 text-sm">Tiempo en la Plataforma</p>
                  <p className="text-2xl font-bold">{calculateTimeOnPlatform(user?.created_at)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Properties Table */}
          <div className="bg-white rounded-xl shadow-lg border border-amber-200 p-6">
            <h3 className="text-xl font-bold text-amber-800 mb-4">Detalle de Propiedades</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-amber-200">
                <thead className="bg-amber-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Direcci&oacute;n
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Calificaci&oacute;n Promedio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Inquilinos
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Evaluaciones
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Inversi&oacute;n
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-amber-200">
                  {portfolioData.properties.map((property, index) => (
                    <tr key={index} className="hover:bg-amber-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-amber-900">{property.address}</div>
                        <div className="text-sm text-amber-500">&Uacute;ltima evaluaci&oacute;n: {property.latest_rating}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-amber-900 mr-2">
                            {property.average_rating}/5
                          </span>
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <svg
                                key={star}
                                className={`w-4 h-4 ${
                                  star <= property.average_rating ? 'text-yellow-400' : 'text-gray-300'
                                }`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-amber-900">
                        {property.unique_tenants}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-amber-900">
                        {property.total_ratings}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-amber-900">
                        ${property.total_invested}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          {property.disputes > 0 && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              {property.disputes} disputa(s)
                            </span>
                          )}
                          {property.evidence_files > 0 && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {property.evidence_files} archivo(s)
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Evaluations View */}
      {activeView === 'evaluations' && evaluationsData && (
        <div className="space-y-6">
          {/* Evaluations Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center">
                <svg className="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-amber-100 text-sm">N&uacute;mero de Evaluaciones</p>
                  <p className="text-3xl font-bold">{evaluationsData.total_evaluations || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex items-center">
                <svg className="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
                <div>
                  <p className="text-green-100 text-sm">Promedio de Calificaciones</p>
                  <p className="text-3xl font-bold">{evaluationsData.average_rating || 0}/5</p>
                </div>
              </div>
            </div>
          </div>

          {/* All Evaluations List */}
          <div className="bg-white rounded-xl shadow-lg border border-amber-200">
            <div className="px-6 py-4 border-b border-amber-200">
              <h3 className="text-lg font-semibold text-amber-900"> Todas tus Evaluaciones</h3>
              <p className="text-amber-600 text-sm">Datos completos de todos los inquilinos evaluados</p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-amber-200">
                <thead className="bg-amber-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Inquilino
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Contacto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Calificaci&oacute;n Global
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Criterios Detallados
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-amber-700 uppercase tracking-wider">
                      Fecha & Costo
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-amber-200">
                  {evaluationsData.evaluations && evaluationsData.evaluations.map((evaluation, index) => (
                    <tr key={index} className="hover:bg-amber-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-amber-900">{evaluation.tenant_name}</div>
                        <div className="text-xs text-amber-600">ID: {evaluation.tenant_id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-amber-900">{evaluation.tenant_email}</div>
                        <div className="text-xs text-amber-600">{evaluation.tenant_phone || 'No disponible'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="text-lg font-bold text-amber-900">{evaluation.overall_rating}/5</div>
                          <div className="ml-2 flex">
                            {[...Array(5)].map((_, i) => (
                              <svg key={i} className={`w-4 h-4 ${i < evaluation.overall_rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-xs space-y-1">
                          <div className="flex justify-between">
                            <span className="text-amber-600">Pago Puntual:</span>
                            <span className="font-medium">{evaluation.payment_history || 'N/A'}/5</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-amber-600">Cuidado Propiedad:</span>
                            <span className="font-medium">{evaluation.property_care || 'N/A'}/5</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-amber-600">Comunicaci&oacute;n:</span>
                            <span className="font-medium">{evaluation.communication || 'N/A'}/5</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-amber-600">Ruido:</span>
                            <span className="font-medium">{evaluation.noise_level || 'N/A'}/5</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-amber-600">Respeto Normas:</span>
                            <span className="font-medium">{evaluation.rule_compliance || 'N/A'}/5</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-amber-600">Estado Final:</span>
                            <span className="font-medium">{evaluation.cleanliness || 'N/A'}/5</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-amber-600">Recomendable:</span>
                            <span className="font-medium">{evaluation.recommendation || 'N/A'}/5</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-amber-900">{new Date(evaluation.created_at).toLocaleDateString('es-ES')}</div>
                        <div className="text-xs text-amber-600 mt-1">
                          {evaluation.evidence_files && evaluation.evidence_files.length > 0 && (
                            <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded">
                               {evaluation.evidence_files.length} archivo(s)
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Show message if no evaluations */}
            {!evaluationsData.evaluations || evaluationsData.evaluations.length === 0 && (
              <div className="text-center py-8">
                <div className="text-amber-600 text-lg mb-2"></div>
                <p className="text-amber-700">No has realizado evaluaciones a&uacute;n</p>
                <p className="text-amber-600 text-sm">Las evaluaciones aparecer&aacute;n aqu&iacute; una vez que eval&uacute;es a tus primeros inquilinos.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Enhanced Statistical Insights View */}
      {activeView === 'insights' && (
        <div className="space-y-8">
          {console.log(' Insights view - enhancedInsights:', enhancedInsights)}
          {console.log(' Insights view - condition result:', activeView === 'insights' && enhancedInsights)}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6 text-white shadow-lg">
            <h3 className="text-xl font-bold mb-2"> An&aacute;lisis Estad&iacute;stico Avanzado</h3>
            <p className="text-amber-100">Insights detallados basados en an&aacute;lisis estad&iacute;stico puro - Sin predicciones de IA</p>
          </div>

          {enhancedInsights ? (
            <>
              {/* Statistical Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl shadow-lg border border-amber-200 p-6">
                  <h4 className="text-lg font-bold text-amber-800 mb-4"> Resumen Estad&iacute;stico</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-amber-700">Total Evaluaciones:</span>
                      <span className="font-bold text-amber-900">{enhancedInsights.statistical_overview?.total_evaluations}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-amber-700">Inversi&oacute;n Total:</span>
                      <span className="font-bold text-green-600">${enhancedInsights.statistical_overview?.total_investment}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-amber-700">Promedio Otorgado:</span>
                      <span className="font-bold text-blue-600">{enhancedInsights.statistical_overview?.average_rating_given}/5</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-amber-700">Eficiencia Evaluativa:</span>
                      <span className="font-bold text-purple-600">{enhancedInsights.statistical_overview?.evaluation_efficiency}</span>
                    </div>
                  </div>
                </div>

                {/* Rating Distribution */}
                <div className="bg-white rounded-xl shadow-lg border border-amber-200 p-6">
                  <h4 className="text-lg font-bold text-amber-800 mb-4"> Distribuci&oacute;n de Calificaciones</h4>
                  <div className="space-y-2">
                    {enhancedInsights.rating_distribution?.quality_segments && (
                      <>
                        <div className="flex justify-between items-center py-2 border-b border-amber-100">
                          <span className="text-green-700">Excelentes (4-5)</span>
                          <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-bold">
                            {enhancedInsights.rating_distribution.quality_segments.excellent_4_5_stars}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-amber-100">
                          <span className="text-yellow-700">Buenas (3)</span>
                          <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-bold">
                            {enhancedInsights.rating_distribution.quality_segments.good_3_stars}
                          </span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-red-700">Problem&aacute;ticas (1-2)</span>
                          <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-bold">
                            {enhancedInsights.rating_distribution.quality_segments.needs_attention_1_2_stars}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Temporal Analysis */}
                <div className="bg-white rounded-xl shadow-lg border border-amber-200 p-6">
                  <h4 className="text-lg font-bold text-amber-800 mb-4"> An&aacute;lisis Temporal</h4>
                  <div className="space-y-3">
                    {enhancedInsights.temporal_analysis && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-amber-700">Mes M&aacute;s Activo:</span>
                          <span className="font-bold text-amber-900">{enhancedInsights.temporal_analysis.most_active_month}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-amber-700">Promedio Mensual:</span>
                          <span className="font-bold text-blue-600">{enhancedInsights.temporal_analysis.evaluation_frequency_per_month}</span>
                        </div>
                        <div className="bg-amber-50 p-3 rounded-lg mt-4">
                          <p className="text-sm text-amber-700">
                            <strong>Actividad:</strong> Has estado activo en {Object.keys(enhancedInsights.temporal_analysis.monthly_activity || {}).length} meses diferentes
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Category Performance */}
              <div className="bg-white rounded-xl shadow-lg border border-amber-200 p-6">
                <h4 className="text-lg font-bold text-amber-800 mb-4"> Rendimiento por Categor&iacute;a</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {enhancedInsights.category_performance && Object.entries(enhancedInsights.category_performance).map(([category, stats]) => {
                    const categoryNames = {
                      'pago_puntual': 'Pago Puntual',
                      'cuidado_propiedad': 'Cuidado de la Propiedad',
                      'comunicacion': 'Comunicaci\u00f3n',
                      'limpieza': 'Limpieza',
                      'nivel_ruido': 'Nivel de Ruido',
                      'cumplimiento_normas': 'Cumplimiento de Normas'
                    };
                    
                    return (
                      <div key={category} className="bg-amber-50 p-4 rounded-lg">
                        <h5 className="font-semibold text-amber-800 mb-2">
                          {categoryNames[category] || category}
                        </h5>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span>Promedio:</span>
                            <span className="font-bold text-blue-600">{stats.average}/5</span>
                          </div>
                          <div className="flex justify-between">
                            <span>M&aacute;s Alto:</span>
                            <span className="text-green-600">{stats.highest}/5</span>
                          </div>
                          <div className="flex justify-between">
                            <span>M&aacute;s Bajo:</span>
                            <span className="text-red-600">{stats.lowest}/5</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Investment Analysis */}
              <div className="bg-white rounded-xl shadow-lg border border-amber-200 p-6">
                <h4 className="text-lg font-bold text-amber-800 mb-4"> An&aacute;lisis de Inversi&oacute;n</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 mb-2">
                      {calculateTimeOnPlatform(user?.created_at)}
                    </div>
                    <div className="text-sm text-green-700">Tiempo en la Plataforma</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 mb-2">
                      ${enhancedInsights.investment_analysis?.cost_per_property}
                    </div>
                    <div className="text-sm text-blue-700">Costo por Propiedad</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 mb-2">
                      {enhancedInsights.investment_analysis?.evaluation_density}
                    </div>
                    <div className="text-sm text-purple-700">Densidad de Evaluaci&oacute;n</div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl shadow-lg border border-amber-200 p-6">
              <p className="text-amber-600 text-center">
                No hay datos estad&iacute;sticos disponibles. Genera datos de muestra para ver los an&aacute;lisis.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Comparative Statistics View */}
      {activeView === 'comparative' && (
        <div className="space-y-8">
          {console.log(' Comparative view - comparativeStats:', comparativeStats)}
          <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl p-6 text-white shadow-lg">
            <h3 className="text-xl font-bold mb-2"> An&aacute;lisis Comparativo</h3>
            <p className="text-blue-100">Compara tu desempe&ntilde;o con los promedios de la plataforma</p>
          </div>

          {comparativeStats ? (
            <>
              {/* Platform Benchmarks */}
              <div className="bg-white rounded-xl shadow-lg border border-blue-200 p-6">
                <h4 className="text-lg font-bold text-blue-800 mb-4"> Datos de la Plataforma</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 mb-2">
                      {comparativeStats.platform_benchmarks.total_landlords}
                    </div>
                    <div className="text-sm text-blue-700">Total Propietarios</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 mb-2">
                      {comparativeStats.platform_benchmarks.total_evaluations}
                    </div>
                    <div className="text-sm text-green-700">Total Evaluaciones</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 mb-2">
                      {comparativeStats.evaluation_volume?.your_evaluations}
                    </div>
                    <div className="text-sm text-purple-700">Tus Evaluaciones</div>
                  </div>
                </div>
              </div>

              {/* Comparative Analysis */}
              {comparativeStats.comparative_analysis && (
                <div className="bg-white rounded-xl shadow-lg border border-blue-200 p-6">
                  <h4 className="text-lg font-bold text-blue-800 mb-4"> Comparativa por Categor&iacute;as</h4>
                  <div className="space-y-4">
                    {Object.entries(comparativeStats.comparative_analysis).map(([category, comparison]) => (
                      <div key={category} className={`p-4 rounded-lg border-l-4 ${
                        comparison.performance === 'above_average' ? 'bg-green-50 border-green-400' :
                        comparison.performance === 'below_average' ? 'bg-red-50 border-red-400' :
                        'bg-gray-50 border-gray-400'
                      }`}>
                        <div className="flex justify-between items-center">
                          <h5 className="font-semibold text-gray-800 capitalize">
                            {category.replace(/_/g, ' ')}
                          </h5>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            comparison.performance === 'above_average' ? 'bg-green-100 text-green-800' :
                            comparison.performance === 'below_average' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {comparison.performance === 'above_average' ? 'Por Encima' :
                             comparison.performance === 'below_average' ? 'Por Debajo' : 'Promedio'}
                          </span>
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-2 sm:gap-4 text-sm">
                          <div className="text-center">
                            <div className="font-bold text-blue-600">{comparison.your_average}</div>
                            <div className="text-gray-600">Tu Promedio</div>
                          </div>
                          <div className="text-center">
                            <div className="font-bold text-gray-600">{comparison.platform_average}</div>
                            <div className="text-gray-600">Promedio Plataforma</div>
                          </div>
                          <div className="text-center">
                            <div className={`font-bold ${comparison.difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {comparison.difference >= 0 ? '+' : ''}{comparison.difference}
                            </div>
                            <div className="text-gray-600">Diferencia</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Legal Disclaimer */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-amber-500 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h5 className="font-bold text-amber-800 mb-2"> An&aacute;lisis Estad&iacute;stico Puro</h5>
                    <p className="text-amber-700 text-sm">
                      Todos los an&aacute;lisis mostrados est&aacute;n basados en <strong>estad&iacute;sticas puras</strong> y <strong>datos hist&oacute;ricos</strong>. 
                      No utilizamos inteligencia artificial para realizar predicciones, recomendaciones automatizadas o clasificaciones 
                      de riesgo de inquilinos. Esta informaci&oacute;n es &uacute;nicamente para fines informativos y de an&aacute;lisis estad&iacute;stico.
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl shadow-lg border border-blue-200 p-6">
              <p className="text-blue-600 text-center">
                No hay datos comparativos disponibles. Genera datos de muestra para ver los an&aacute;lisis.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { AnalyticsDashboard };
